import React, { useState, useEffect } from 'react'
import { Shield, Users, AlertTriangle, Clock, Power, Ban, Eye, UserX, Search, Filter } from 'lucide-react'
import { adminAPI, UserActivity } from '../services/api'

const AdminSecurity: React.FC = () => {
  const [users, setUsers] = useState<UserActivity[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    total_users: 0,
    online_users: 0,
    blocked_users: 0,
    pending_logouts: 0
  })
  const [selectedUser, setSelectedUser] = useState<UserActivity | null>(null)
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [blockReason, setBlockReason] = useState('')
  
  // Filtry
  const [userSearch, setUserSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [ipDeviceSearch, setIpDeviceSearch] = useState('')
  const [licenseFilter, setLicenseFilter] = useState('all')

  useEffect(() => {
    fetchUserActivities()
    // Odświeżaj co 30 sekund
    const interval = setInterval(fetchUserActivities, 30000)
    return () => clearInterval(interval)
  }, [])

  // Efekt filtrowania
  useEffect(() => {
    let filtered = users

    // Filtr wyszukiwania użytkownika po nazwie/email
    if (userSearch.trim()) {
      const searchLower = userSearch.toLowerCase()
      filtered = filtered.filter(user => 
        user.user_name?.toLowerCase().includes(searchLower) ||
        user.user_email?.toLowerCase().includes(searchLower)
      )
    }

    // Filtr statusu
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.online_status === statusFilter)
    }

    // Filtr IP/urządzenia
    if (ipDeviceSearch.trim()) {
      const searchLower = ipDeviceSearch.toLowerCase()
      filtered = filtered.filter(user => 
        user.ip_address?.toLowerCase().includes(searchLower) ||
        user.device_info?.toLowerCase().includes(searchLower)
      )
    }

    // Filtr licencji
    if (licenseFilter !== 'all') {
      filtered = filtered.filter(user => user.license_status === licenseFilter)
    }

    setFilteredUsers(filtered)
  }, [users, userSearch, statusFilter, ipDeviceSearch, licenseFilter])

  const fetchUserActivities = async () => {
    try {
      setLoading(true)
      const response = await adminAPI.getUserActivities()
      const userData = response.data.activities || []
      
      setUsers(userData)
      setFilteredUsers(userData)
      setStats({
        total_users: userData.length,
        online_users: userData.filter((u: UserActivity) => u.online_status === 'online').length,
        blocked_users: userData.filter((u: UserActivity) => u.license_status === 'blocked').length,
        pending_logouts: userData.filter((u: UserActivity) => u.force_logout_pending).length
      })
    } catch (error) {
      console.error('Error fetching user activities:', error)
      // Fallback to empty data on error
      setUsers([])
      setFilteredUsers([])
      setStats({
        total_users: 0,
        online_users: 0,
        blocked_users: 0,
        pending_logouts: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-600/10 text-green-400 border-green-900/50'
      case 'idle':
        return 'bg-yellow-600/10 text-yellow-400 border-yellow-900/50'
      case 'offline':
        return 'bg-gray-600/10 text-gray-400 border-gray-900/50'
      default:
        return 'bg-gray-600/10 text-gray-400 border-gray-900/50'
    }
  }

  const getLicenseStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-600/10 text-green-400 border-green-900/50'
      case 'blocked':
        return 'bg-red-600/10 text-red-400 border-red-900/50'
      case 'suspended':
        return 'bg-orange-600/10 text-orange-400 border-orange-900/50'
      case 'trial':
        return 'bg-blue-600/10 text-blue-400 border-blue-900/50'
      default:
        return 'bg-gray-600/10 text-gray-400 border-gray-900/50'
    }
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const formatLastSeen = (lastHeartbeat: string) => {
    const diff = Date.now() - new Date(lastHeartbeat).getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    
    if (minutes < 1) return 'Teraz'
    if (minutes < 60) return `${minutes}m temu`
    
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h temu`
    
    const days = Math.floor(hours / 24)
    return `${days}d temu`
  }

  const handleForceLogout = async (user: UserActivity) => {
    try {
      await adminAPI.forceLogout(user.user_id)
      alert(`Wylogowanie dla ${user.user_email} zostało zaplanowane`)
      
      // Update local state
      setUsers(users.map(u => 
        u.user_id === user.user_id 
          ? { ...u, force_logout_pending: true }
          : u
      ))
    } catch (error) {
      console.error('Error forcing logout:', error)
      alert('Błąd podczas wymuszania wylogowania')
    }
  }

  const handleBlockUser = async () => {
    if (!selectedUser || !blockReason.trim()) return

    try {
      await adminAPI.blockUser(selectedUser.user_id, blockReason)
      alert(`Użytkownik ${selectedUser.user_email} został zablokowany`)
      
      // Update local state
      setUsers(users.map(u => 
        u.user_id === selectedUser.user_id 
          ? { ...u, license_status: 'blocked' as const, online_status: 'offline' as const }
          : u
      ))
      
      setShowBlockModal(false)
      setSelectedUser(null)
      setBlockReason('')
    } catch (error) {
      console.error('Error blocking user:', error)
      alert('Błąd podczas blokowania użytkownika')
    }
  }

  const handleUnblockUser = async (user: UserActivity) => {
    try {
      await adminAPI.unblockUser(user.user_id)
      alert(`Użytkownik ${user.user_email} został odblokowany`)
      
      // Update local state
      setUsers(users.map(u => 
        u.user_id === user.user_id 
          ? { ...u, license_status: 'active' as const }
          : u
      ))
    } catch (error) {
      console.error('Error unblocking user:', error)
      alert('Błąd podczas odblokowywania użytkownika')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 p-6">
        <div className="flex items-center justify-center">
          <div className="text-white">Ładowanie danych bezpieczeństwa...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
          <Shield className="h-8 w-8 text-primary mr-3" />
          Zabezpieczenia Real-Time
        </h1>
        <p className="text-gray-400">Monitorowanie aktywności użytkowników i zarządzanie bezpieczeństwem</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-blue-600/10 rounded-lg">
              <Users className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Łącznie użytkowników</p>
              <p className="text-2xl font-bold text-white">{stats.total_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-green-600/10 rounded-lg">
              <Clock className="h-8 w-8 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Online teraz</p>
              <p className="text-2xl font-bold text-white">{stats.online_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-red-600/10 rounded-lg">
              <Ban className="h-8 w-8 text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Zablokowani</p>
              <p className="text-2xl font-bold text-white">{stats.blocked_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-orange-600/10 rounded-lg">
              <AlertTriangle className="h-8 w-8 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Oczekujące wylogowania</p>
              <p className="text-2xl font-bold text-white">{stats.pending_logouts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtry */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-primary mr-2" />
          <h3 className="text-lg font-semibold text-white">Filtry</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Wyszukiwanie użytkownika */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Wyszukaj użytkownika
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Nazwa lub email..."
                className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtr statusu */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Wszystkie statusy</option>
              <option value="online">Online</option>
              <option value="idle">Bezczynny</option>
              <option value="offline">Offline</option>
            </select>
          </div>

          {/* Wyszukiwanie IP/urządzenia */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              IP / Urządzenie
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={ipDeviceSearch}
                onChange={(e) => setIpDeviceSearch(e.target.value)}
                placeholder="IP lub nazwa urządzenia..."
                className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtr licencji */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Licencja
            </label>
            <select
              value={licenseFilter}
              onChange={(e) => setLicenseFilter(e.target.value)}
              className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Wszystkie licencje</option>
              <option value="active">Aktywna</option>
              <option value="blocked">Zablokowana</option>
              <option value="suspended">Zawieszona</option>
              <option value="trial">Trial</option>
            </select>
          </div>
        </div>

        {/* Statystyki filtrowanych wyników */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Pokazano {filteredUsers.length} z {users.length} użytkowników
          </div>
          
          {/* Przycisk czyszczenia filtrów */}
          {(userSearch || statusFilter !== 'all' || ipDeviceSearch || licenseFilter !== 'all') && (
            <button
              onClick={() => {
                setUserSearch('')
                setStatusFilter('all')
                setIpDeviceSearch('')
                setLicenseFilter('all')
              }}
              className="text-sm text-primary hover:text-primary-light transition-colors"
            >
              Wyczyść filtry
            </button>
          )}
        </div>
      </div>

      {/* Users Activity Table */}
      <div className="bg-dark-800 rounded-lg overflow-hidden border border-dark-700">
        <div className="px-6 py-4 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Eye className="h-5 w-5 text-primary mr-2" />
            Aktywność użytkowników
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Użytkownik</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Status</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Ostatnia aktywność</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Czas sesji</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">IP / Urządzenie</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Licencja</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="border-b border-dark-600 hover:bg-dark-700">
                  <td className="py-4 px-6">
                    <div>
                      <div className="text-white font-medium">{user.user_name}</div>
                      <div className="text-gray-400 text-sm">{user.user_email}</div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(user.online_status)}`}>
                      {user.online_status === 'online' && 'Online'}
                      {user.online_status === 'idle' && 'Bezczynny'}
                      {user.online_status === 'offline' && 'Offline'}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-300">
                    {user.last_heartbeat ? formatLastSeen(user.last_heartbeat) : 'Nigdy'}
                  </td>
                  <td className="py-4 px-6 text-gray-300">
                    {user.session_duration && user.session_duration > 0 ? formatDuration(user.session_duration) : '-'}
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <div className="text-gray-300 text-sm">{user.ip_address || 'Nieznane'}</div>
                      <div className="text-gray-500 text-xs">{user.device_info || 'Nieznane'}</div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getLicenseStatusColor(user.license_status)}`}>
                      {user.license_status === 'active' && 'Aktywna'}
                      {user.license_status === 'blocked' && 'Zablokowana'}
                      {user.license_status === 'suspended' && 'Zawieszona'}
                      {user.license_status === 'trial' && 'Trial'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex space-x-2">
                      {user.license_status !== 'blocked' && (
                        <>
                          <button
                            onClick={() => handleForceLogout(user)}
                            disabled={user.force_logout_pending}
                            className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1 disabled:opacity-50"
                          >
                            <Power className="h-3 w-3" />
                            {user.force_logout_pending ? 'Oczekuje...' : 'Wyloguj'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setShowBlockModal(true)
                            }}
                            className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                          >
                            <Ban className="h-3 w-3" />
                            Zablokuj
                          </button>
                        </>
                      )}
                      {user.license_status === 'blocked' && (
                        <button
                          onClick={() => handleUnblockUser(user)}
                          className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                        >
                          <UserX className="h-3 w-3" />
                          Odblokuj
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Block User Modal */}
      {showBlockModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <Ban className="h-6 w-6 text-red-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">
                Zablokuj użytkownika
              </h3>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                Czy na pewno chcesz zablokować użytkownika?
              </p>
              <div className="bg-dark-700 p-3 rounded border">
                <div className="text-white font-medium">{selectedUser.user_name}</div>
                <div className="text-gray-400 text-sm">{selectedUser.user_email}</div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Powód blokady *
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="input w-full h-20 resize-none"
                placeholder="Wpisz powód blokady użytkownika..."
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowBlockModal(false)
                  setSelectedUser(null)
                  setBlockReason('')
                }}
                className="flex-1 py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleBlockUser}
                disabled={!blockReason.trim()}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zablokuj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSecurity